import {Component, OnInit} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {TranslateModule} from '@ngx-translate/core';
import {RouterModule} from '@angular/router';
import {ApiAgentService} from '../../services/api-agent.service';

/**
 * PDPL "Download my data" (Roadmap #28 / SAMA Obs #11).
 * The merchant re-enters their password, receives an OTP on their registered
 * phone and email, and downloads their personal-data ZIP exactly once.
 */
@Component({
  selector: 'app-download-my-data',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule, RouterModule],
  templateUrl: './download-my-data.component.html'
})
export class DownloadMyDataComponent implements OnInit {

  // none | awaiting-otp | ready | done
  step = 'none';
  password = '';
  otp = '';
  submitting = false;
  error: string = null;
  info: string = null;
  private business: any = null;

  constructor(private api: ApiAgentService) {
  }

  async ngOnInit() {
    try {
      this.business = await this.api.getMyBusiness(false);
    } catch (e) {
      // best effort; the download falls back to a generic file name
    }
    try {
      const status: any = await this.api.getDataCopyStatus();
      if (status && (status.status === 'awaiting-otp' || status.status === 'ready')) {
        this.step = 'awaiting-otp';
      }
    } catch (e) {
      // stay on the request step
    }
  }

  /** <merchant identity>-<date>.zip — named after the merchant, no product prefix. */
  private downloadFileName(): string {
    const raw = (this.business?.businessName || this.business?.licenseName
      || this.business?.licenseNumber || 'merchant') + '';
    const id = raw.trim().replace(/[^\p{L}\p{N}]+/gu, '-').replace(/^-+|-+$/g, '').slice(0, 40) || 'merchant';
    const date = new Date().toISOString().slice(0, 10);
    return `${id}-${date}.zip`;
  }

  async request() {
    this.error = null;
    this.info = null;
    if (!this.password) {
      this.error = 'Your account password is required.';
      return;
    }
    this.submitting = true;
    try {
      await this.api.requestDataCopy(this.password);
      this.password = '';
      this.step = 'awaiting-otp';
      this.info = 'A verification code was sent to your registered phone and email.';
    } catch (e) {
      this.error = this.messageOf(e, 'Could not start your data copy. Check your password and try again.');
    } finally {
      this.submitting = false;
    }
  }

  async resend() {
    this.error = null;
    this.info = null;
    this.submitting = true;
    try {
      await this.api.resendDataCopyOtp();
      this.info = 'A verification code was sent to your registered phone and email.';
    } catch (e) {
      this.error = this.messageOf(e, 'Could not send a new verification code.');
    } finally {
      this.submitting = false;
    }
  }

  async download() {
    this.error = null;
    this.info = null;
    if (!this.otp) {
      this.error = 'Enter the verification code.';
      return;
    }
    this.submitting = true;
    try {
      const blob = await this.api.downloadDataCopy(this.otp.trim());
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = this.downloadFileName();
      anchor.click();
      URL.revokeObjectURL(url);
      this.otp = '';
      this.step = 'done';
    } catch (e) {
      if (e && e.status === 400) {
        this.error = 'Incorrect or expired verification code.';
      } else {
        this.error = 'This data copy is no longer available.';
        this.step = 'none';
      }
    } finally {
      this.submitting = false;
    }
  }

  private messageOf(e: any, fallback: string): string {
    if (e && e.error && typeof e.error === 'object' && e.error.message) {
      return e.error.message;
    }
    return fallback;
  }
}
